import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import branding from '../../styles/branding';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './PromptModificationModal.css';

const PromptModificationModal = ({
  open,
  onClose,
  widgetType,
  currentPrompt,
  onSavePrompt,
  isLoading = false
}) => {
  const [modifiedPrompt, setModifiedPrompt] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (open && currentPrompt) {
      setModifiedPrompt(currentPrompt);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [open, currentPrompt]);

  useEffect(() => {
    if (saveError) {
      showErrorToast(saveError);
      setSaveError(null);
    }
  }, [saveError, showErrorToast]);

  const handleSave = async () => {
    if (!modifiedPrompt.trim()) {
      setSaveError('Prompt cannot be empty');
      return;
    }

    setSaveError(null);
    setSaveSuccess(false);

    try {
      await onSavePrompt(modifiedPrompt.trim());
      setSaveSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setSaveError(getApiErrorMessage(error, 'Failed to save prompt'));
    }
  };

  const handleClose = () => {
    setModifiedPrompt('');
    setSaveError(null);
    setSaveSuccess(false);
    onClose();
  };

  const getWidgetTypeDisplayName = (type) => {
    const typeMappings = {
      'recommendation': 'AI Recommendation',
      'summary': 'Summarize Data',
      'summarize': 'Summarize Data',
      'analysis': 'Analyze Major Issues',
      'analyze': 'Analyze Major Issues',
      'success': 'Success Factors',
      'success_factors': 'Success Factors',
      'custom_ai_card': 'Custom AI Card'
    };
    return typeMappings[type] || type || 'Unknown Card';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={branding.dialog?.PaperProps}
      slotProps={branding.dialog?.slotProps}
      className="prompt-modal"
    >
      <DialogTitle className="prompt-modal-title">
        Modify Prompt - {getWidgetTypeDisplayName(widgetType)}
      </DialogTitle>
      <DialogContent className="prompt-modal-content">
        <Box className="prompt-modal-body">
          <Typography variant="caption" className="prompt-modal-description">
            Customize the AI prompt for this widget. The modified prompt will be used when executing AI analysis.
          </Typography>


          {saveSuccess && (
            <Alert severity="success" className="prompt-modal-alert">
              Prompt saved successfully!
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={15}
            label="AI Prompt"
            value={modifiedPrompt}
            onChange={(e) => setModifiedPrompt(e.target.value)}
            placeholder="Enter your custom AI prompt here..."
            variant="outlined"
            disabled={isLoading}
            helperText={`${modifiedPrompt.length} characters`}
            className="prompt-modal-textfield"
          />

          <Box className="prompt-modal-tips">
            <Typography variant="caption" className="prompt-modal-tips-title">
              <strong>Tips:</strong>
            </Typography>
            <Typography variant="caption" className="prompt-modal-tip">
              • Be specific about what you want the AI to analyze
            </Typography>
            <Typography variant="caption" className="prompt-modal-tip">
              • Include instructions about the output format (JSON structure)
            </Typography>
            <Typography variant="caption" className="prompt-modal-tip">
              • Specify any constraints (e.g., number of points, length requirements)
            </Typography>
            <Typography variant="caption" className="prompt-modal-tip">
              • Mention that the analysis should be based only on the provided data
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="prompt-modal-actions">
        <Button
          variant={branding.button.variant}
          size={branding.button.size}
          sx={branding.button.sx}
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant={branding.button.variant}
          size={branding.button.size}
          sx={branding.button.sx}
          onClick={handleSave}
          disabled={isLoading || !modifiedPrompt.trim() || saveSuccess}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Prompt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptModificationModal;
